import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowRight, Clock, Lock } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { PatriciaWidget } from "@/components/PatriciaWidget";
import { Reveal } from "@/components/Reveal";
import { SEO } from "@/components/SEO";
import { ToolModal } from "@/components/tools/ToolModal";
import { tools as baseTools, filterCategories, type ToolCategory, type Tool } from "@/data/tools";
import type { ToolKey } from "@/data/types";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";
import { useFeatureFlags } from "@/hooks/useFeatureFlags";
import { useSiteImages } from "@/hooks/useSiteImages";
import { DEFAULT_PRESCRIBED_RATE } from "@/lib/moraInterest";

const STATUTES = [
  "Companies Act 71 of 2008",
  "POPIA, Act 4 of 2013",
  "Prescribed Rate of Interest Act 55 of 1975",
  "Commissioners of Oaths Act 16 of 1963",
  "FICA, Act 38 of 2001",
  "B-BBEE Act 53 of 2003",
];

const HOW_STEPS = [
  {
    n: "01",
    title: "Pick a tool",
    desc: "Choose from a growing suite of specialised legal tools built for South African law.",
  },
  {
    n: "02",
    title: "Fill in the details",
    desc: "Capture the facts, parties, and instructions in plain language.",
  },
  {
    n: "03",
    title: "Casey drafts",
    desc: "Casey produces a jurisdiction-specific draft that cites the right statutes.",
  },
  {
    n: "04",
    title: "Review and finalise",
    desc: "Review, refine, settle, and sign. Export as PDF or DOCX.",
  },
];

const TIERS = [
  {
    name: "Solo",
    who: "Individuals, candidate attorneys, and sole practitioners.",
    includes: "Patricia I for affidavits and letters of demand.",
    price: "From R490/mo",
  },
  {
    name: "Professional",
    who: "Established practices and in-house teams.",
    includes: "Patricia II: full Patricia I (affidavits, letters of demand, resolutions, agreements) plus the Conditions Precedent Tracker, Due Diligence, and the Compliance Agent.",
    price: "From R1,490/mo",
  },
  {
    name: "Enterprise",
    who: "Large firms, chambers, and deal teams.",
    includes: "Custom scoping, integrations, and dedicated support.",
    price: "Custom",
  },
];

/** Refero-register console: real instructions, each deep-linking to the
 * underlying Patricia capability (DESIGN.md v3.1 instruction console). */
const CONSOLE_PROMPTS: { text: string; tool: ToolKey; title: string }[] = [
  { text: "Founding affidavit for an urgent application", tool: "affidavit", title: "Affidavits" },
  { text: "Letter of demand: unpaid invoice, R120,000, mora interest", tool: "lod", title: "Letters of Demand" },
  { text: "Section 3 notice to an organ of state", tool: "lod", title: "Letters of Demand" },
  { text: "Section 60 written resolution of shareholders", tool: "resolution", title: "Resolutions" },
  { text: "Employment contract under the BCEA, 3-month probation", tool: "agreement", title: "Agreements" },
  { text: "NDA ahead of a due diligence", tool: "agreement", title: "Agreements" },
];

const STATS = [
  { value: DEFAULT_PRESCRIBED_RATE, decimals: 1, label: "% p.a. prescribed rate, verified to the day" },
  { value: 7, decimals: 0, label: "statutes encoded as product logic" },
  { value: 14, decimals: 0, label: "day professional trial, no card required" },
];

/** Count-up stat (DESIGN.md motion system). Animates once on scroll into view. */
const StatCounter = ({ value, decimals, label }: { value: number; decimals: number; label: string }) => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      el.textContent = value.toFixed(decimals);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const t0 = performance.now();
          const tick = (t: number) => {
            const p = Math.min((t - t0) / 1200, 1);
            el.textContent = (value * (1 - Math.pow(1 - p, 3))).toFixed(decimals);
            if (p < 1) requestAnimationFrame(tick);
          };
          requestAnimationFrame(tick);
          io.unobserve(entry.target);
        });
      },
      { threshold: 0.5 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [value, decimals]);

  return (
    <div className="border-r border-hairline p-9 last:border-r-0 max-md:border-b max-md:border-r-0 max-md:last:border-b-0">
      <div ref={ref} className="font-serif text-[3.2rem] font-extrabold leading-none tracking-tight text-foreground">
        0
      </div>
      <div className="label-uppercase mt-3">{label}</div>
    </div>
  );
};

const Tools = () => {
  const [filter, setFilter] = useState<"all" | ToolCategory>("all");
  const [activeTool, setActiveTool] = useState<ToolKey | null>(null);
  const { user } = useAuth();
  const { flags } = useFeatureFlags();
  const { images } = useSiteImages();
  const navigate = useNavigate();
  const { toolId } = useParams<{ toolId?: string }>();

  // Merge DB feature flags over the hardcoded defaults. Flags allow admins to
  // toggle a tool's coming-soon status (or hide it entirely) without a deploy.
  const tools: Tool[] = useMemo(() => {
    return baseTools
      .map((t) => {
        const flag = flags[t.id];
        if (!flag) return t;
        if (!flag.enabled) return null;
        return { ...t, comingSoon: flag.coming_soon };
      })
      .filter((t): t is Tool => t !== null);
  }, [flags]);

  const filtered = useMemo(() => (filter === "all" ? tools : tools.filter((t) => t.category === filter)), [filter, tools]);

  const liveCount = useMemo(() => tools.filter((t) => !t.comingSoon).length, [tools]);

  // Deep-link: /tool/:toolId opens the tool modal directly. Requires the user
  // to be signed in and the tool to be live; otherwise we fall back to a toast.
  useEffect(() => {
    if (!toolId) return;
    const target = tools.find((t) => t.id === toolId);
    if (!target) {
      navigate("/tools", { replace: true });
      return;
    }
    if (target.comingSoon) {
      toast(`${target.title}: in development`, {
        description: "This tool is still in development. We'll notify trial users on launch.",
      });
      navigate("/tools", { replace: true });
      return;
    }
    if (!user) {
      // Preserve intent so the user lands back here after sign-in.
      sessionStorage.setItem("pendingTool", target.id);
      toast("Sign in required", {
        description: `${target.title} unlocks after you sign in.`,
        action: { label: "Sign in", onClick: () => navigate("/login") },
      });
      navigate("/tools", { replace: true });
      return;
    }
    setActiveTool(target.id as ToolKey);
  }, [toolId, tools, user, navigate]);

  // Honour pendingTool stored before the user signed in.
  useEffect(() => {
    if (!user || activeTool) return;
    const pending = sessionStorage.getItem("pendingTool");
    if (!pending) return;
    sessionStorage.removeItem("pendingTool");
    const target = tools.find((t) => t.id === pending && !t.comingSoon);
    if (target) setActiveTool(target.id as ToolKey);
  }, [user, tools, activeTool]);

  // Parallax drift on the quote-band image (DESIGN.md motion system).
  const bandRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const band = bandRef.current;
        const img = band?.querySelector("img");
        if (band && img) {
          const r = band.getBoundingClientRect();
          const p = Math.min(Math.max((window.innerHeight - r.top) / (window.innerHeight + r.height), 0), 1);
          img.style.transform = `translateY(${(p - 0.5) * -14}%)`;
        }
        ticking = false;
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleToolClick = (toolId: string, title: string, comingSoon?: boolean) => {
    if (comingSoon) {
      toast(`${title}: in development`, {
        description: "This tool is still in development. We'll notify trial users on launch.",
      });
      return;
    }
    // Patricia is the console, not a single form: send the click there and let
    // the instruction chips route to the underlying capability.
    if (toolId === "patricia") {
      document.getElementById("console")?.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }
    if (!user) {
      toast("Sign in required", {
        description: `${title} unlocks after you sign in. Start your 14-day free trial.`,
        action: { label: "Sign in", onClick: () => navigate("/login") },
      });
      return;
    }
    setActiveTool(toolId as ToolKey);
  };

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="Legal automation tools for South African lawyers · Casey Legal Tools"
        description="Draft affidavits, letters of demand, resolutions, and agreements built for South African law. Trial free for 14 days."
        path="/tools"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          name: "Casey Legal Tools",
          applicationCategory: "BusinessApplication",
          operatingSystem: "Web",
          offers: { "@type": "Offer", price: "490", priceCurrency: "ZAR" },
        }}
      />
      <Navbar />

      <main>
        {/* Hero — light monochrome poster. SEO H1 lives here. */}
        <section className="mx-auto max-w-content px-8 pb-16 pt-24 md:pt-28">
          <h1 className="max-w-[1000px] font-serif text-[clamp(3rem,7.5vw,6.5rem)] font-extrabold leading-[0.98] tracking-tight text-foreground">
            <span className="hero-line">
              <span>Serious drafting,</span>
            </span>
            <span className="hero-line">
              <span>
                under <em>our</em> law.
              </span>
            </span>
          </h1>
          <div className="mt-11 flex flex-wrap items-end justify-between gap-10">
            <p className="max-w-[520px] text-[1.08rem] leading-[1.7] text-muted-foreground">
              Affidavits, letters of demand, resolutions and agreements, drafted and reviewed by AI
              that knows the <b className="font-semibold text-foreground">Companies Act</b> from the
              inside, with every deadline computed to the{" "}
              <b className="font-semibold text-foreground">court day</b>.
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <Link
                to={user ? "/dashboard" : "/signup"}
                className="group inline-flex items-center gap-2.5 rounded-btn border border-foreground bg-foreground px-7 py-3.5 text-[0.92rem] font-medium text-background transition-colors hover:bg-background hover:text-foreground"
              >
                {user ? "Open your dashboard" : "Start drafting"}
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" strokeWidth={2} />
              </Link>
              <Link
                to="/pricing"
                className="inline-flex items-center rounded-btn border border-hairline px-7 py-3.5 text-[0.92rem] font-medium text-foreground transition-colors hover:bg-foreground hover:text-background"
              >
                View pricing
              </Link>
            </div>
          </div>
        </section>

        {/* Marquee — the seven instruments */}
        <div className="overflow-hidden border-y border-hairline py-4" aria-hidden="true">
          <div className="marquee-track">
            {[0, 1].map((dup) => (
              <span key={dup}>
                {tools.map((t) => (
                  <span key={`${dup}-${t.id}`} className="mx-7 font-mono text-[0.72rem] uppercase tracking-[0.16em] text-muted-foreground">
                    {t.title} <span className="mx-7 text-hairline">·</span>
                  </span>
                ))}
              </span>
            ))}
          </div>
        </div>

        {/* Hero photography — Johannesburg, grayscale, Ken Burns */}
        <div className="relative h-[62vh] overflow-hidden">
          <img
            src={images.hero.url}
            alt={images.hero.alt}
            className="kenburns img-grade h-[120%] w-full object-cover"
          />
          <div className="absolute inset-x-0 bottom-0 flex items-center justify-between px-8 pb-6">
            <span className="font-mono text-[0.66rem] uppercase tracking-[0.16em] text-white mix-blend-difference">
              Johannesburg · 26.2041° S, 28.0473° E
            </span>
            <span className="font-mono text-[0.66rem] uppercase tracking-[0.16em] text-white mix-blend-difference">
              {liveCount} tools live · {tools.length - liveCount} in development
            </span>
          </div>
        </div>

        {/* Statute strip */}
        <section className="border-b border-hairline">
          <div className="mx-auto flex max-w-content flex-wrap items-center gap-x-7 gap-y-2 px-8 py-5">
            <span className="label-uppercase flex-shrink-0">Grounded in</span>
            {STATUTES.map((s) => (
              <span key={s} className="font-mono text-[0.72rem] text-ink-mid/80">
                {s}
              </span>
            ))}
          </div>
        </section>

        {/* How it works */}
        <section className="border-b border-hairline">
          <div className="mx-auto max-w-content px-8 py-20">
            <div className="mb-12 grid gap-6 md:grid-cols-[1fr_2fr]">
              <Reveal>
                <div className="eyebrow">01 / How it works</div>
              </Reveal>
              <Reveal delay={1}>
                <h2 className="font-serif text-[clamp(1.8rem,3.4vw,2.8rem)] font-extrabold leading-[1.05] tracking-tight text-foreground">
                  From instruction to <em>signed</em> document.
                </h2>
              </Reveal>
            </div>
            <div className="grid grid-cols-1 gap-x-10 gap-y-8 sm:grid-cols-2 lg:grid-cols-4">
              {HOW_STEPS.map((step, i) => (
                <Reveal key={step.n} delay={Math.min(i, 3) as 1 | 2 | 3}>
                  <div className="border-t border-hairline pt-5">
                    <div className="mb-3 font-mono text-[0.72rem] tracking-[0.14em] text-muted-foreground">{step.n}</div>
                    <div className="mb-1.5 text-[0.95rem] font-semibold text-foreground">{step.title}</div>
                    <p className="text-[0.84rem] leading-[1.65] text-muted-foreground">{step.desc}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* Product index — numbered rows, hover inversion */}
        <section className="mx-auto max-w-content px-8 py-20">
          <div className="mb-10 flex flex-wrap items-end justify-between gap-6">
            <div className="grid gap-4">
              <Reveal>
                <div className="eyebrow">02 / The instruments</div>
              </Reveal>
              <Reveal delay={1}>
                <h2 className="font-serif text-[clamp(1.8rem,3.4vw,2.8rem)] font-extrabold leading-[1.05] tracking-tight text-foreground">
                  Four tools. <em>One</em> standard of care.
                </h2>
              </Reveal>
            </div>
            <div className="flex flex-wrap items-center gap-1.5">
              {filterCategories.map((c) => {
                const active = filter === c.value;
                return (
                  <button
                    key={c.value}
                    onClick={() => setFilter(c.value)}
                    className={cn(
                      "rounded-chip border px-3.5 py-1.5 font-mono text-[0.68rem] uppercase tracking-[0.1em] transition-colors",
                      active
                        ? "border-foreground bg-foreground text-background"
                        : "border-hairline bg-transparent text-muted-foreground hover:border-foreground hover:text-foreground",
                    )}
                  >
                    {c.label}
                  </button>
                );
              })}
            </div>
          </div>

          <Reveal className="border-t border-hairline">
            {filtered.map((tool, i) => (
              <article
                key={tool.id}
                onClick={() => handleToolClick(tool.id, tool.title, tool.comingSoon)}
                className={cn(
                  "group grid cursor-pointer grid-cols-[52px_1fr_56px] items-center gap-6 border-b border-hairline px-3 py-8 transition-colors duration-300 hover:bg-foreground md:grid-cols-[64px_1fr_340px_56px]",
                  tool.comingSoon && "opacity-70",
                )}
              >
                <span className="font-mono text-[0.72rem] text-muted-foreground transition-colors group-hover:text-background/60">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <h3 className="font-serif text-[clamp(1.3rem,2.2vw,1.7rem)] font-bold tracking-tight text-foreground transition-colors group-hover:text-background">
                  {tool.title}
                  <span className="ml-4 hidden rounded-chip border border-current px-2 py-0.5 align-middle font-mono text-[0.55rem] font-normal uppercase tracking-[0.14em] opacity-60 sm:inline-block">
                    {tool.comingSoon ? "In development" : tool.categoryLabel}
                  </span>
                </h3>
                <p className="hidden text-[0.86rem] leading-[1.6] text-muted-foreground transition-colors group-hover:text-background/70 md:block">
                  {tool.description}
                </p>
                <span className="justify-self-end text-foreground transition-all group-hover:translate-x-1.5 group-hover:text-background">
                  {tool.comingSoon ? (
                    <Clock className="h-4 w-4" strokeWidth={1.8} />
                  ) : !user ? (
                    <Lock className="h-4 w-4" strokeWidth={1.8} />
                  ) : (
                    <ArrowRight className="h-5 w-5" strokeWidth={1.8} />
                  )}
                </span>
              </article>
            ))}
          </Reveal>
        </section>

        {/* Patricia console — the product as the hero object (refero.design register) */}
        <section id="console" className="scroll-mt-20 bg-foreground text-background">
          <div className="mx-auto max-w-content px-8 py-24">
            <div className="mx-auto max-w-[820px] text-center">
              <Reveal>
                <div className="eyebrow justify-center text-background/50">03 / The instruction</div>
              </Reveal>
              <Reveal delay={1}>
                <h2 className="mt-5 font-serif text-[clamp(1.9rem,3.8vw,3rem)] font-extrabold leading-[1.03] tracking-tight">
                  Brief Patricia like you'd brief a <em>junior.</em>
                </h2>
              </Reveal>
            </div>

            <Reveal delay={2}>
              <div className="mx-auto mt-12 max-w-[780px] rounded-card border border-background/15 bg-white/[0.05] p-5 sm:p-7">
                <div className="mb-4 flex items-center gap-2.5 font-mono text-[0.62rem] uppercase tracking-[0.16em] text-background/50">
                  <span className="inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-background" />
                  Patricia · Drafting clerk
                </div>
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    navigate(user ? "/dashboard" : "/signup");
                  }}
                  className="flex items-center gap-2 rounded-btn border border-background/25 bg-foreground px-5 py-3"
                >
                  <input
                    type="text"
                    placeholder="Demand payment of R84,300 due since 14 March, with mora interest…"
                    className="w-full bg-transparent text-[0.9rem] text-background placeholder:text-background/40 focus:outline-none"
                    aria-label="Instruct Patricia"
                  />
                  <button
                    type="submit"
                    aria-label="Send instruction"
                    className="grid h-8 w-8 flex-shrink-0 place-items-center rounded-btn bg-background text-foreground transition-transform hover:translate-x-0.5"
                  >
                    <ArrowRight className="h-4 w-4" strokeWidth={2} />
                  </button>
                </form>
                <div className="mt-6 font-mono text-[0.62rem] uppercase tracking-[0.16em] text-background/40">
                  Popular instructions
                </div>
                <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {CONSOLE_PROMPTS.map((p) => (
                    <button
                      key={p.text}
                      type="button"
                      onClick={() => handleToolClick(p.tool, p.title)}
                      className="group flex items-center justify-between gap-3 rounded-chip border border-background/20 px-4 py-2.5 text-left text-[0.8rem] text-background/75 transition-colors hover:border-background hover:bg-background hover:text-foreground"
                    >
                      <span>{p.text}</span>
                      <ArrowRight className="h-3.5 w-3.5 flex-shrink-0 opacity-0 transition-all group-hover:translate-x-0.5 group-hover:opacity-100" strokeWidth={2} />
                    </button>
                  ))}
                </div>
              </div>
            </Reveal>
          </div>
        </section>

        {/* Stats trio */}
        <section className="mx-auto max-w-content px-8 pb-20 pt-20">
          <Reveal className="grid border border-hairline md:grid-cols-3">
            {STATS.map((s) => (
              <StatCounter key={s.label} {...s} />
            ))}
          </Reveal>
        </section>

        {/* Quote band — law library, parallax */}
        <div ref={bandRef} className="relative h-[440px] overflow-hidden">
          <img
            src={images.quote_band.url}
            alt={images.quote_band.alt}
            className="img-grade-dark h-[130%] w-full object-cover will-change-transform"
          />
          <div className="absolute inset-0 flex max-w-[900px] flex-col justify-center px-8 md:px-20">
            <Reveal>
              <p className="font-serif text-[clamp(1.4rem,2.6vw,2.1rem)] font-bold leading-[1.25] tracking-tight text-white">
                "It knows our law. Not translated law. Ours. The section 3 notice came out right
                the first time."
              </p>
            </Reveal>
            <Reveal delay={1}>
              <div className="mt-5 font-mono text-[0.68rem] uppercase tracking-[0.18em] text-white/70">
                Director, Commercial Litigation, Johannesburg
              </div>
            </Reveal>
          </div>
        </div>

        {/* Tiers teaser */}
        <section className="border-b border-hairline">
          <div className="mx-auto max-w-content px-8 py-20">
            <div className="mb-10 flex flex-wrap items-end justify-between gap-6">
              <div className="grid gap-4">
                <Reveal>
                  <div className="eyebrow">04 / Plans</div>
                </Reveal>
                <Reveal delay={1}>
                  <h2 className="font-serif text-[clamp(1.8rem,3.4vw,2.8rem)] font-extrabold leading-[1.05] tracking-tight text-foreground">
                    Three tiers. Every tier includes the ones <em>below</em> it.
                  </h2>
                </Reveal>
              </div>
              <Link
                to="/pricing"
                className="group inline-flex items-center gap-2 text-[0.9rem] font-medium text-foreground"
              >
                Compare plans
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" strokeWidth={2} />
              </Link>
            </div>
            <Reveal className="grid grid-cols-1 border border-hairline md:grid-cols-3">
              {TIERS.map((tier) => (
                <Link
                  key={tier.name}
                  to="/pricing#compare"
                  className="group flex flex-col border-b border-hairline p-9 transition-colors duration-300 last:border-b-0 hover:bg-foreground md:border-b-0 md:border-r md:last:border-r-0"
                >
                  <div className="label-uppercase mb-4 transition-colors group-hover:text-background/60">{tier.name}</div>
                  <div className="mb-5 font-serif text-[1.7rem] font-extrabold leading-none tracking-tight text-foreground transition-colors group-hover:text-background">
                    {tier.price}
                  </div>
                  <p className="text-[0.84rem] leading-[1.65] text-muted-foreground transition-colors group-hover:text-background/70">{tier.who}</p>
                  <p className="mt-2 flex-1 text-[0.84rem] leading-[1.65] text-ink-mid transition-colors group-hover:text-background/80">{tier.includes}</p>
                  <span className="mt-6 inline-flex items-center gap-1.5 text-[0.82rem] font-medium text-foreground transition-colors group-hover:text-background">
                    See what's included
                    <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" strokeWidth={2} />
                  </span>
                </Link>
              ))}
            </Reveal>
          </div>
        </section>

        {/* Security and controls band */}
        <section className="bg-foreground text-background">
          <div className="mx-auto max-w-content px-8 py-24">
            <div className="grid gap-10 md:grid-cols-[1fr_1.3fr] md:gap-16">
              <div className="grid content-start gap-4">
                <Reveal>
                  <div className="eyebrow text-background/50">05 / Security</div>
                </Reveal>
                <Reveal delay={1}>
                  <h2 className="max-w-[400px] font-serif text-[clamp(1.8rem,3.4vw,2.8rem)] font-extrabold leading-[1.05] tracking-tight">
                    Enterprise-grade <em>security</em> and controls
                  </h2>
                </Reveal>
              </div>
              <div>
                <Reveal delay={1}>
                  <p className="max-w-[560px] text-[0.98rem] leading-[1.7] text-background/70">
                    Casey handles privileged work, so the controls firms and in-house teams
                    expect come standard: SAML SSO, audit logs, IP allow-listing, and data
                    lifecycle management, with role-based access on every plan.
                  </p>
                </Reveal>
                <Reveal delay={2}>
                  <Link
                    to="/security"
                    className="group mt-7 inline-flex items-center gap-2 rounded-btn border border-background/30 px-5 py-2.5 text-[0.85rem] font-medium text-background transition-colors hover:bg-background hover:text-foreground"
                  >
                    More about security
                    <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" strokeWidth={2} />
                  </Link>
                </Reveal>
              </div>
            </div>
            <Reveal delay={2}>
              <div className="mt-14 grid grid-cols-2 gap-x-8 gap-y-6 md:grid-cols-4">
                {[
                  { label: "POPIA", note: "Compliant by design" },
                  { label: "Privilege", note: "Work product protected" },
                  { label: "Encryption", note: "In transit and at rest" },
                  { label: "No AI training", note: "Your documents stay yours" },
                ].map((b) => (
                  <div key={b.label} className="border-t border-background/15 pt-4">
                    <div className="font-mono text-[0.68rem] uppercase tracking-[0.14em] text-background/80">
                      {b.label}
                    </div>
                    <div className="mt-1.5 text-[0.8rem] text-background/50">{b.note}</div>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </section>

        {/* Closing CTA */}
        <section className="mx-auto max-w-content px-8 py-28 text-center">
          <Reveal>
            <div className="eyebrow justify-center">06 / Fourteen days, on us</div>
          </Reveal>
          <Reveal delay={1}>
            <h2 className="mx-auto mt-5 max-w-[720px] font-serif text-[clamp(2.2rem,5vw,4rem)] font-extrabold leading-[1.02] tracking-tight text-foreground">
              Take on the work you used to <em>turn away.</em>
            </h2>
          </Reveal>
          <Reveal delay={2}>
            <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
              <Link
                to={user ? "/dashboard" : "/signup"}
                className="group inline-flex items-center gap-2.5 rounded-btn border border-foreground bg-foreground px-7 py-3.5 text-[0.92rem] font-medium text-background transition-colors hover:bg-background hover:text-foreground"
              >
                {user ? "Open your dashboard" : "Start free trial"}
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" strokeWidth={2} />
              </Link>
              <Link
                to="/pricing"
                className="inline-flex items-center rounded-btn border border-hairline px-7 py-3.5 text-[0.92rem] font-medium text-foreground transition-colors hover:bg-foreground hover:text-background"
              >
                View pricing
              </Link>
            </div>
          </Reveal>
        </section>
      </main>

      <Footer />
      <PatriciaWidget />
      <ToolModal toolKey={activeTool} onClose={() => setActiveTool(null)} />
    </div>
  );
};

export default Tools;
